import { Navigation } from "@/components/layout/Navigation";
import Hero from "@/components/marketing/Hero";
import Footer from "@/components/layout/Footer";
import { Lightbulb, GraduationCap, Newspaper, Pen } from 'lucide-react';
import CTAButton from "@/components/marketing/CTAButton";
import Testimonial from "@/components/marketing/Testimonial";
import { MarketingFactCheckDemo, MarketingTinderAngleDemo } from '@/components/marketing-demos';
import { Badge } from '@/components/ui/badge';
import { Earth } from 'lucide-react';

const ForYou = () => {
  return (
    <div className="min-h-screen">
      <Navigation showAuth={true} className="mt-4" />

      {/* Hero Section */}
      <Hero
        badge="Your Own AI Writing Coach"
        headline={
          <>Let Arcitext assist,{" "}
            <span className="font-serif font-medium italic text-primary">
              not replace you.
            </span>
          </>
        }
        description={<><span className="block mb-2">Nobody tells your stories like you.</span><span className="block mb-2">Whether you're creating a personal brand, or just writing for fun, Arcitext will help you write better and clearer.</span></>}
        heroVisuals={< div className="mt-8 w-full relative lg:mt-0 flex flex-col gap-4" ><div className="w-[320px] mx-auto lg:absolute z-10 lg:top-1/2 lg:-translate-y-1/2 lg:-right-5 "><MarketingTinderAngleDemo /></div><MarketingFactCheckDemo className="relative z-5 bg-transparent p-0 max-w-xs lg:bg-background hidden lg:block" /></div >}
      >
        <CTAButton
          to="/auth"
          event="For Creators Hero CTA"
        />
      </Hero >

      <Testimonial quote="Arcitext is gonna be super useful for content creators and copywriters." author="@AlxTurovski" link="https://x.com/alxturovski/status/1976542015614517402?s=46&t=j4uhSoy1QJuJFaWk06ohwg" linkText="Read the tweet" />

      {/* Personal Use Cases */}
      <section className="pb-16 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-12">
            <h2 className="text-3xl font-bold text-foreground">Perfect for Any Writing Purpose</h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="glass-card flex flex-col items-center p-6 rounded-2xl space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Structured Writing</h3>
                <p className="text-muted-foreground">
                  Find your writing style, and stay consistent. Get rewrite suggestions that fit your style and purpose.
                </p>
              </div>

              <div className="glass-card flex flex-col items-center p-6 rounded-2xl space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center">
                  <Newspaper className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Articles & Blogs</h3>
                <p className="text-muted-foreground">
                  Write content that sound like you. Anywhere. Fact-check any claims, and back them up with credible sources.
                </p>
              </div>

              <div className="glass-card flex flex-col items-center p-6 rounded-2xl space-y-4">
                <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Personal Brands</h3>
                <p className="text-muted-foreground">
                  Maintain your authentic writing style while scaling content creation. Use Discovery to get content ideas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Helps You */}
      <section className="py-16 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              No quirks. It just works.
            </h2>

            <div className="space-y-8">
              <div className="flex gap-6 items-start">
                <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-semibold text-sm">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Discover Your Writing Style</h3>
                  <p className="text-muted-foreground">
                    Upload writing samples or provide URLs to your published writing. Arcitext analyzes and profiles your writing style, so you can maintain it.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-semibold text-sm">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Write With Confidence</h3>
                  <p className="text-muted-foreground">
                    Add context to your documents, and let Arcitext give you feedback when you need it. Arcitext can suggest rewrites, do fact-checks, and analyze your writing style fit.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-semibold text-sm">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Discover New Topics</h3>
                  <p className="text-muted-foreground">
                    Arcitext's Discovery tool helps you get rid of writer's block. Simply add your favorite RSS feeds, and let Arcitext suggest angles that fits your purpose and audience.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-16">
            <CTAButton
              to="/auth"
              event="For Creators How It Works CTA"
            />
          </div>
        </div>
      </section>

      {/* Personal Benefits */}
      {/* <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-12">
            <h2 className="text-3xl font-bold text-foreground">Why Personal Writers Love Arcitext</h2>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto text-left">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">No More Writer's Block</h3>
                <p className="text-muted-foreground">
                  Get unstuck with personalized suggestions that feel like they came from you. Arcitext helps you find the right words when you're struggling.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Authentic Voice</h3>
                <p className="text-muted-foreground">
                  Never sound generic again. Arcitext ensures your personality shines through every piece you write, making your content uniquely yours.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Gentle Guidance</h3>
                <p className="text-muted-foreground">
                  Get supportive feedback that encourages creativity rather than stifling it. Arcitext is like having a thoughtful friend review your work.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Learn & Improve</h3>
                <p className="text-muted-foreground">
                  Understand what makes your writing effective. Arcitext helps you grow as a writer while staying true to who you are.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <Footer />
    </div >
  );
};

export default ForYou;
